/**
 * HTML DOM处理模块
 * @module dom
 */

const SPECIAL_CHARS_REGEXP = /([:\-_]+(.))/g;
const MOZ_HACK_REGEXP = /^moz([A-Z])/;

const camelCase = function (name) {
  return name.replace(SPECIAL_CHARS_REGEXP, function (_, separator, letter, offset) {
    return offset ? letter.toUpperCase() : letter;
  }).replace(MOZ_HACK_REGEXP, 'Moz$1');
};

/**
 * 添加元素样式class
 * @param {HTMLElement} el
 * @param {string} cls
 */
export function addClass(el, cls) {
  el?.classList?.add(cls);
}

/**
 * 删除元素样式class
 * @param {HTMLElement} el
 * @param {string} cls
 */
export function removeClass(el, cls) {
  el?.classList?.remove(cls);
}

/**
 * 元素是否有样式class
 * @param {HTMLElement} el
 * @param {string} cls
 */
export function hasClass(el, cls) {
  return el?.classList?.contains(cls);
}

/**
 * 获取元素样式style
 * @param {HTMLElement} el
 * @param {string} styleName
 * @returns {null|string}
 */
export function getStyle(el, styleName) {
  if (!el || !styleName) return null;
  styleName = camelCase(styleName);
  if (styleName === 'float') {
    styleName = 'cssFloat';
  }
  try {
    const computed = document.defaultView.getComputedStyle(el, '');
    return el.style[styleName] || computed ? computed[styleName] : null;
  } catch (e) {
    return el.style[styleName];
  }
}

/**
 * 设置元素样式style
 * @param {HTMLElement} el
 * @param {string|Object} styleName
 * @param {string} [value] 当styleName为Object时，不需要传value
 */
export function setStyle(el, styleName, value) {
  if (!el || !styleName) return;

  if (typeof styleName === 'object') {
    for (const prop in styleName) {
      if (Object.prototype.hasOwnProperty.call(styleName, prop)) {
        setStyle(el, prop, styleName[prop]);
      }
    }
  } else {
    styleName = camelCase(styleName);
    el.style[styleName] = value;
  }
}

/**
 * 绑定元素事件
 * @param {HTMLElement} el
 * @param {string} event 事件名称
 * @param {function} handler
 */
export function on(el, event, handler) {
  el?.addEventListener(event, handler, false);
}

/**
 * 销毁元素事件
 * @param {HTMLElement} el
 * @param {string} event 事件名称
 * @param {function} handler
 */
export function off(el, event, handler) {
  el?.removeEventListener(event, handler, false);
}

/**
 * 绑定元素事件，只响应一次
 * @param {HTMLElement} el
 * @param {string} event 事件名称
 * @param {function} handler
 */
export function once(el, event, handler) {
  const listener = function () {
    if (handler) {
      handler.apply(this, arguments);
    }
    off(el, event, listener);
  };
  on(el, event, listener);
}

/**
 * 在目标元素的后面插入新的元素
 * @param {HTMLElement} targetEl
 * @param {HTMLElement} newEl
 */
export function insertAfter(targetEl, newEl) {
  const parentEl = targetEl.parentNode;
  if (parentEl.lastChild === targetEl) {
    parentEl.appendChild(newEl);
  } else {
    parentEl.insertBefore(newEl, targetEl.nextSibling);
  }
}

export function isDomRoot(el) {
  return [window, document, document.documentElement, null, undefined].includes(el);
}

export const isScroll = (el, vertical) => {
  const determinedDirection = ![null, undefined].includes(vertical);
  const overflow = determinedDirection
    ? vertical
      ? getStyle(el, 'overflow-y')
      : getStyle(el, 'overflow-x')
    : getStyle(el, 'overflow');

  return overflow.match(/(scroll|auto)/);
};

/**
 * 获取目标元素的滚动容器
 * @param {HTMLElement} [el] 目标元素，默认 document
 * @param {boolean} [vertical] 指定垂直方向，true表示垂直， false表示水平，空或null表示auto
 * @return {HTMLElement|Window}
 */
export function getScrollContainer(el, vertical) {
  let parent = el;
  while (parent) {
    if (isDomRoot(parent)) {
      return window;
    }
    if (isScroll(parent, vertical)) {
      return parent;
    }
    parent = parent.parentNode;
  }

  return parent;
}

/**
 * 判断元素是否在容器之内
 * @param {HTMLElement} el 目标元素
 * @param {HTMLElement} container 容器元素
 * @return {boolean|boolean}
 */
export function isInContainer(el, container) {
  if (!el || !container) return false;

  const elRect = el.getBoundingClientRect();
  const containerRect = isDomRoot(container)
    ? {
      top: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
      left: 0
    }
    : container.getBoundingClientRect();

  return elRect.top < containerRect.bottom &&
    elRect.bottom > containerRect.top &&
    elRect.right > containerRect.left &&
    elRect.left < containerRect.right;
}

/**
 * 注入style到页面
 * @param {string} content
 * @return {HTMLStyleElement}
 */
export function injectStyle(content) {
  const style = document.createElement('style');
  style.setAttribute('type', 'text/css');
  style.innerHTML = content;
  const head = document.querySelector('head');
  if (head) {
    head.appendChild(style);
  }
  return style;
}

export default {
  addClass,
  removeClass,
  hasClass,
  getStyle,
  setStyle,
  on,
  off,
  once,
  insertAfter,
  getScrollContainer,
  isInContainer,
  injectStyle
};
